import type { Registry as Services } from '@ember/service';
import type { RouterContract, SearchParams } from 'shared-react';

/**
 * Resolves a `$`-placeholder path against the current path. Each `$name` segment
 * is replaced by the matching key in `params`, falling back to the value at the
 * same position in the current path (so `$hotelId` tracks the hotel in view).
 * Throws when a placeholder is satisfied by neither, so callers never receive a
 * path that still contains a `$` token.
 *
 * @example
 * // currentPath: /hotel/42/settings
 * resolvePath('/hotel/42/settings', '/hotel/$hotelId/settings/personal');
 * // => '/hotel/42/settings/personal'
 *
 * @example
 * // currentPath: /hotel/42/overview
 * resolvePath('/hotel/42/overview', '/hotel/$hotelId/$section', { section: 'settings' });
 * // => '/hotel/42/settings'
 */
function resolvePath(
  currentPath: string,
  to: string,
  params?: Record<string, string>,
): string {
  const segments = currentPath.split('/');
  const missing: string[] = [];

  const path = to
    .split('/')
    .map((segment, index) => {
      if (!segment.startsWith('$')) {
        return segment;
      }
      const key = segment.slice(1);
      const value = params?.[key] ?? segments[index];
      if (value === undefined) {
        missing.push(key);
        return segment;
      }
      return value;
    })
    .join('/');

  if (missing.length > 0) {
    throw new Error(`Missing route params: ${missing.join(',')}`);
  }
  return path;
}

function appendSearch(path: string, search?: SearchParams): string {
  if (!search) {
    return path;
  }
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(search)) {
    if (value === null || value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        query.append(key, item);
      }
    } else {
      query.append(key, String(value));
    }
  }
  const queryString = query.toString();
  return queryString ? `${path}?${queryString}` : path;
}

function toRouteNamePrefix(recognizedName: string): string {
  return recognizedName.replace(/\.index$/, '');
}

/**
 * Translates Ember's RouterService into compatibility-router's contract, the
 * layer mounted React packages route through.
 */
export default function createCompatibilityRouter(
  router: Services['router'],
): RouterContract {
  const [currentPath = ''] = (router.currentURL ?? '').split('?');

  return {
    buildHref: ({ to, params, search }): string =>
      appendSearch(resolvePath(currentPath, to, params), search),
    isActive: ({ to, params }): boolean => {
      const target = router.recognize(resolvePath(currentPath, to, params));
      return target ? router.isActive(toRouteNamePrefix(target.name)) : false;
    },
    navigate: ({ to, params, search, replace }): void => {
      const url = appendSearch(resolvePath(currentPath, to, params), search);
      if (replace) {
        router.replaceWith(url);
      } else {
        router.transitionTo(url);
      }
    },
  };
}
